import { canonicalSlugForId } from "./canonical-tour-slugs";
import { formatPriceIn, getCurrency } from "./currency";
import { getDisplayLocale } from "./display-locale";

// Convert a Bokun product title + id into a SEO-friendly slug
// and recover the trailing id from a slug.

export function slugify(input: string): string {
  return input
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80);
}

export function makeTourSlug(title: string, id: string | number): string {
  const pinned = canonicalSlugForId(String(id));
  if (pinned) return pinned;
  const base = slugify(title || "tour");
  return `${base}-${id}`;
}

export function extractIdFromSlug(slug: string): string | null {
  const m = slug.match(/-(\d+)$/);
  if (m) return m[1];
  // Allow the raw id as slug too.
  if (/^\d+$/.test(slug)) return slug;
  return null;
}

export function formatDuration(minutes: number | null): string | null {
  if (!minutes || minutes <= 0) return null;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h && m) return `${h}h ${m}m`;
  if (h) return `${h}h`;
  return `${m}m`;
}

export function formatPrice(amount: number | null, currency: string | null): string {
  // Presentation-only conversion into the user's selected display currency.
  // The product's original amount/currency (Bokun) is never modified.
  return formatPriceIn(amount, currency, getCurrency(), { locale: getDisplayLocale() });
}

export function stripHtml(html: string | null | undefined): string {
  if (!html) return "";
  return html
    .replace(/<style[\s\S]*?<\/style>/gi, "")
    .replace(/<script[\s\S]*?<\/script>/gi, "")
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/p>/gi, "\n\n")
    .replace(/<li[^>]*>/gi, "• ")
    .replace(/<\/li>/gi, "\n")
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'")
    .replace(/&#39;/g, "'")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    // Numeric decimal entities, e.g. &#127754; → 🌊
    .replace(/&#(\d+);/g, (_, n) => {
      try { return String.fromCodePoint(parseInt(n, 10)); } catch { return ""; }
    })
    // Numeric hex entities, e.g. &#x1F30A; → 🌊
    .replace(/&#x([0-9a-fA-F]+);/g, (_, n) => {
      try { return String.fromCodePoint(parseInt(n, 16)); } catch { return ""; }
    })
    .replace(/[ \t]+/g, " ")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}
